// parallaxUtils.ts
import { breakpoints } from '@msc-b2c/shared-utils';

export function getCurrentBreakpoint(): string {
  const width = window.innerWidth;
  if (width >= 1920) return 'xxl';
  if (width >= breakpoints.xxl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= 768) return 'md';
  return 'sm';
}

export function isFullscreen(): boolean {
  const doc = document as any;

  // Standard & desktop Safari
  if (document.fullscreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement) {
    return true;
  }

  // iOS Safari: native video fullscreen (no document fullscreen element)
  try {
    const anyIOSVideoFS = Array.from(document.querySelectorAll('video')).some(
      (v) => (v as any)?.webkitDisplayingFullscreen === true
    );
    if (anyIOSVideoFS) return true;
  } catch {
    /* no-op */
  }

  return false;
}

export function isElementFullscreen(el: Element | null): boolean {
  if (!el) return false;
  const doc = document as any;
  return (
    document.fullscreenElement === el ||
    doc.webkitFullscreenElement === el ||
    (el as any).webkitDisplayingFullscreen === true // iOS Safari video
  );
}

export function onFullscreenChange(cb: (isOn: boolean) => void) {
  const handler = () => cb(isFullscreen());

  // Standard events
  document.addEventListener('fullscreenchange', handler);
  document.addEventListener('webkitfullscreenchange', handler as any);
  document.addEventListener('msfullscreenchange', handler as any);

  // iOS Safari: listen on video elements
  const attachIOSVideoListeners = () => {
    const videos = Array.from(document.querySelectorAll('video')) as any[];
    videos.forEach((v) => {
      // avoid duplicates if we re-attach
      v.removeEventListener('webkitbeginfullscreen', handler as any);
      v.removeEventListener('webkitendfullscreen', handler as any);
      v.addEventListener('webkitbeginfullscreen', handler as any);
      v.addEventListener('webkitendfullscreen', handler as any);
    });
  };

  // Attach now…
  attachIOSVideoListeners();

  // …and attach for future videos that may be added dynamically
  const mo = new MutationObserver(() => attachIOSVideoListeners());
  mo.observe(document.documentElement, { childList: true, subtree: true });

  return () => {
    document.removeEventListener('fullscreenchange', handler);
    document.removeEventListener('webkitfullscreenchange', handler as any);
    document.removeEventListener('msfullscreenchange', handler as any);
    mo.disconnect();
    const videos = Array.from(document.querySelectorAll('video')) as any[];
    videos.forEach((v) => {
      v.removeEventListener('webkitbeginfullscreen', handler as any);
      v.removeEventListener('webkitendfullscreen', handler as any);
    });
  };
}

// observeDynamicTabs performs a scroll "snap" that must be paused while we are
// programmatically landing on a URL-hash target, so it can't hijack that scroll.
let autoSnapSuppressed = false;
export function setParallaxAutoSnapSuppressed(suppressed: boolean): void {
  autoSnapSuppressed = suppressed;
}

export function getParallaxRestScrollY(): number {
  return 2 * window.innerHeight + 110;
}

export function observeDynamicTabs(): void {
  const tabs = document.querySelector('.dynamicnavigationtabs')?.closest('.cmp-sticky-container');
  const parallax = document.querySelector('.parallax');

  if (!tabs && !parallax) return;

  let parallaxSeenOnce = false;

  const observer = new IntersectionObserver(
    (entries) => {
      if (isFullscreen()) return;
      if (autoSnapSuppressed) return;
      entries.forEach((entry) => {
        const target = entry.target as HTMLElement;

        if (!entry.isIntersecting) return;
        if (isFullscreen()) return;
        const video = document.querySelector<HTMLVideoElement>('.parallax video');

        if (new URLSearchParams(window.location.search).get('parallaxsnap') !== '0') {
          if (target.classList.contains('cmp-sticky-container')) {
            document.body.scrollTo({
              top: getParallaxRestScrollY(),
              behavior: 'smooth',
            });
            video?.pause();
          } else if (target.classList.contains('parallax')) {
            if (!parallaxSeenOnce) {
              parallaxSeenOnce = true;
              return;
            }
            document.body.scrollTo({
              top: window.innerHeight + 110,
              behavior: 'smooth',
            });
            if (video?.closest('.cards__item')?.classList.contains('is-center')) video?.play();
          }
        }
      });
    },
    {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    }
  );

  if (tabs) observer.observe(tabs);
  if (parallax) observer.observe(parallax);
}

let stateTransversed = false;
let disableGalleryScroll = false;
let scrollTargetY: number | null = null;

const getScrollY = () =>
  window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

export function scrollListener() {
  if (isFullscreen()) return;

  const headerHeight = document.querySelector('.cmp-bottom-container-dark')?.clientHeight || 0;
  const totalHeight = 2 * window.innerHeight + headerHeight;

  if (stateTransversed && getScrollY() < totalHeight - 50) {
    scrollTargetY = 1 * window.innerHeight;
    disableGalleryScroll = true;
    document.body.scrollTo({ top: scrollTargetY, behavior: 'smooth' });
    stateTransversed = false;
  }

  if (
    disableGalleryScroll &&
    scrollTargetY !== null &&
    Math.abs(getScrollY() - scrollTargetY) < 2
  ) {
    disableGalleryScroll = false;
    scrollTargetY = null;
  }
}

export function setPositionTitleMobile() {
  const bg = document.querySelector<HTMLElement>('.cmp-parallax__background');
  const mobileTitle = document.querySelector<HTMLElement>('.cmp-parallax__title--mobile');

  if (!bg || !mobileTitle) return;

  const bgRect = bg.getBoundingClientRect();
  const titleRect = mobileTitle.getBoundingClientRect();
  const baseOffset = window.scrollY + bgRect.bottom - (window.scrollY + titleRect.bottom);

  const WRAPPED_ADJUST_PX = 45;
  const adjust = mobileTitle.classList.contains('is-wrapped') ? WRAPPED_ADJUST_PX : 0;

  mobileTitle.style.transform = `translateY(${baseOffset - adjust}px)`;
}

// NEW: export a read helper so other files can use the same logic
export function getPageScrollY(): number {
  return getScrollY();
}

let isSnapping = false;

export function handleArrowKeySnap(e: KeyboardEvent) {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

  if (isFullscreen()) return;
  if (new URLSearchParams(window.location.search).get('parallaxsnap') === '0') return;
  if (isSnapping) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  const tabs = document.querySelector('.dynamicnavigationtabs')?.closest('.cmp-sticky-container');

  if (tabs) {
    const rect = tabs.getBoundingClientRect();
    const vh = window.innerHeight;
    const currentScroll = window.scrollY || document.documentElement.scrollTop;

    if (e.key === 'ArrowDown') {
      const isPeeking = rect.top < vh + 10;
      const isNotSnapped = rect.top > 150;

      if (isPeeking && isNotSnapped) {
        e.preventDefault();

        isSnapping = true;

        const targetPosition = currentScroll + rect.top - 110;

        document.body.scrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });

        const video = document.querySelector<HTMLVideoElement>('.parallax video');
        video?.pause();

        setTimeout(() => {
          isSnapping = false;
        }, 1000);
      }
    } else if (e.key === 'ArrowUp') {
      const isAtTabs = rect.top > -50 && rect.top < 300;

      if (isAtTabs) {
        e.preventDefault();

        isSnapping = true;

        const targetPosition = vh + 110;

        document.body.scrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });

        const video = document.querySelector<HTMLVideoElement>('.parallax video');
        if (video?.closest('.cards__item')?.classList.contains('is-center')) {
          video?.play();
        } else if (video && !video.closest('.cards__item')) {
          video.play();
        }

        setTimeout(() => {
          isSnapping = false;
        }, 1000);
      }
    }
  }
}
